import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@shared/lib/apiClient';
import { useActiveOrganization } from '@shared/context/ActiveOrganizationContext';
export const useDebts = () => {
    const queryClient = useQueryClient();
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    const debtsQuery = useQuery({
        queryKey: ['debts', activeOrganizationId],
        enabled: orgReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/debts');
            return data;
        },
    });
    const summaryQuery = useQuery({
        queryKey: ['debts-summary', activeOrganizationId],
        enabled: orgReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/debts/summary');
            return data;
        },
    });
    const createDebt = useMutation({
        mutationFn: async (dto) => {
            const { data } = await apiClient.post('/debts', dto);
            return data;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['debts', activeOrganizationId] });
            void queryClient.invalidateQueries({ queryKey: ['debts-summary', activeOrganizationId] });
        },
    });
    const payInstallment = useMutation({
        mutationFn: async ({ debtId, installmentId, accountId, }) => {
            const { data } = await apiClient.post(`/debts/${debtId}/installments/${installmentId}/pay`, { accountId });
            return data;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['debts', activeOrganizationId] });
            void queryClient.invalidateQueries({ queryKey: ['debts-summary', activeOrganizationId] });
            void queryClient.invalidateQueries({ queryKey: ['transactions', activeOrganizationId] });
        },
    });
    const unpayInstallment = useMutation({
        mutationFn: async ({ debtId, installmentId }) => {
            const { data } = await apiClient.post(`/debts/${debtId}/installments/${installmentId}/unpay`);
            return data;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['debts', activeOrganizationId] });
            void queryClient.invalidateQueries({ queryKey: ['debts-summary', activeOrganizationId] });
            void queryClient.invalidateQueries({ queryKey: ['transactions', activeOrganizationId] });
        },
    });
    const skipInstallment = useMutation({
        mutationFn: async ({ debtId, installmentId }) => {
            const { data } = await apiClient.post(`/debts/${debtId}/installments/${installmentId}/skip`);
            return data;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['debts', activeOrganizationId] });
            void queryClient.invalidateQueries({ queryKey: ['debts-summary', activeOrganizationId] });
        },
    });
    const payRemaining = useMutation({
        mutationFn: async ({ debtId, accountId }) => {
            const { data } = await apiClient.post(`/debts/${debtId}/pay-remaining`, {
                accountId,
            });
            return data;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['debts', activeOrganizationId] });
            void queryClient.invalidateQueries({ queryKey: ['debts-summary', activeOrganizationId] });
            void queryClient.invalidateQueries({ queryKey: ['transactions', activeOrganizationId] });
        },
    });
    const removeDebt = useMutation({
        mutationFn: async (debtId) => {
            await apiClient.delete(`/debts/${debtId}`);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['debts', activeOrganizationId] });
            void queryClient.invalidateQueries({ queryKey: ['debts-summary', activeOrganizationId] });
        },
    });
    return {
        ...debtsQuery,
        debts: debtsQuery.data ?? [],
        summary: summaryQuery.data,
        isSummaryLoading: summaryQuery.isLoading,
        createDebt,
        payInstallment,
        unpayInstallment,
        skipInstallment,
        payRemaining,
        removeDebt,
    };
};
export const useDebtDetail = (debtId) => {
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    return useQuery({
        queryKey: ['debt', activeOrganizationId, debtId],
        enabled: Boolean(activeOrganizationId) && !isLoadingOrganizations && Boolean(debtId),
        queryFn: async () => {
            const { data } = await apiClient.get(`/debts/${debtId}`);
            return data;
        },
    });
};
